<script setup>
import CaretDownIcon from '@iconify-vue/ph/caret-down';
import { CalendarDate } from '@internationalized/date';
import MonthPicker from '@js/components/pickers/MonthPicker.vue';
import { ErrorMessage, Field } from 'vee-validate';
import { computed } from 'vue';

const model = defineModel();

const {
    fieldId,
    fieldName,
    label,
    required = false,
    hasEmptyOption = false,
    inlineLabel = false,
    size = 'medium',
    maxDate = null,
} = defineProps({
    fieldId: {
        type: String,
        required: true,
    },
    fieldName: {
        type: String,
        required: true,
    },
    label: {
        type: String,
        required: true,
    },
    required: {
        type: Boolean,
        required: false,
    },
    hasEmptyOption: {
        type: Boolean,
        required: false,
    },
    inlineLabel: {
        type: Boolean,
        required: false,
    },
    size: {
        type: String,
        required: false,
    },
    maxDate: {
        type: [Date, String],
        required: false,
    },
});

/**
 * Convertit la valeur du champ ({ month: 0-11, year }) en date calendaire.
 */
function toCalendarDate(value) {
    if (
        !value ||
        value.month === null ||
        value.month === undefined ||
        value.year === null ||
        value.year === undefined
    ) {
        return null;
    }

    return new CalendarDate(value.year, value.month + 1, 1);
}

function parseMaxDate(value) {
    if (!value) {
        return undefined;
    }

    if (value instanceof Date) {
        return new CalendarDate(value.getFullYear(), value.getMonth() + 1, 1);
    }

    const [year, month] = String(value).split('-').map(Number);

    if (!year || !month) {
        return undefined;
    }

    return new CalendarDate(year, month, 1);
}

const maxValue = computed(() => parseMaxDate(maxDate));

const selectedMonth = computed({
    get: () => toCalendarDate(model.value),
    set: (value) => {
        model.value = value
            ? { month: value.month - 1, year: value.year }
            : null;
    },
});
</script>

<template>
    <div
        class="mb-4 flex"
        :class="{
            'flex-row items-center': inlineLabel,
            'flex-col': !inlineLabel,
        }"
    >
        <label
            :for="fieldName"
            class="whitespace-nowrap"
            :class="{
                'mr-2': inlineLabel,
                'mb-2': !inlineLabel,
                'text-sm': size === 'small',
            }"
        >
            {{ label }}
            <sup v-if="required" class="text-red-500">*</sup>
        </label>

        <Field :id="fieldId" :name="fieldName" v-model="model">
            <MonthPicker
                v-model="selectedMonth"
                :max-value="maxValue"
                :size="size"
                :has-empty-option="hasEmptyOption"
            >
                <template
                    #trigger="{
                        label: monthLabel,
                        hasValue,
                        isOpen: isPickerOpen,
                    }"
                >
                    <button
                        type="button"
                        class="flex w-full items-center gap-2 rounded-sm border border-gray-300 ring-1 ring-transparent duration-150 focus:ring-gray-300 focus:outline-hidden"
                        :class="{
                            '!ring-gray-300': isPickerOpen,
                            'ring-transparent': !isPickerOpen,
                            'min-w-[210px] px-5 py-3': size === 'medium',
                            'h-[28px] min-w-[140px] px-3 py-1 text-sm':
                                size === 'small',
                        }"
                    >
                        <span v-if="hasValue" class="capitalize">
                            {{ monthLabel }}
                        </span>
                        <template v-else>&nbsp;</template>

                        <CaretDownIcon
                            class="ml-auto size-4 text-gray-500 duration-150"
                            :class="{ 'rotate-180 transform': isPickerOpen }"
                        />
                    </button>
                </template>
            </MonthPicker>
        </Field>

        <ErrorMessage :name="fieldName" class="mt-1 text-sm text-red-500" />
    </div>
</template>
